import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  CommercialOrderSource,
  PosBundleComponent,
  PosCategory,
  PosProduct,
  PosProductAddon,
  PosThemeKey,
  PosThemeMode,
  RecentPosOrder,
  WorkspacePosSettings,
  WorkspaceSummary,
} from '@adminpages/domain-models';

const tokenStorageKey = 'adminpages.pos.apiToken';
const workspaceStorageKey = 'adminpages.pos.workspaceId';
const cashRegisterStorageKey = 'adminpages.pos.cashRegisterIds';
const apiBaseStorageKey = 'adminpages.pos.apiBaseUrl';

export interface PosLoginPayload {
  email: string;
  password: string;
  deviceName: string;
  workspaceId?: number;
}

export interface PosOrderItemPayload {
  product_id?: number;
  product_uuid?: string;
  slug?: string;
  sku?: string;
  quantity: number;
  selected_addon_ids?: number[];
}

export interface PosCreateOrderPayload {
  customer?: PosOrderCustomerPayload;
  items: PosOrderItemPayload[];
  payment?: {
    method: 'cash' | 'transfer';
    amount: number;
    status: 'approved' | 'pending';
    reference?: string;
  };
  customer_notes?: string;
}

export interface PosOrderCustomerPayload {
  id?: number;
  uuid?: string;
  name?: string;
  email?: string;
  phone?: string;
  notes?: string;
}

export interface PosClient {
  id: number;
  uuid?: string;
  name: string;
  companyName?: string;
  email?: string;
  phone?: string;
  taxId?: string;
  notes?: string;
  status: string;
}

export interface PosCreateClientPayload {
  name?: string;
  email?: string;
  phone?: string;
  notes?: string;
}

export interface PosAddOrderPaymentPayload {
  method: 'cash' | 'transfer';
  amount: number;
  status: 'approved' | 'pending';
  reference?: string;
  notes?: string;
}

export interface PosCashSession {
  id: number;
  uuid: string;
  folio: string;
  status: 'open' | 'closed';
  openedByUser?: string;
  closedByUser?: string;
  openedAt: string;
  closedAt?: string;
  openingCashAmount: number;
  expectedCashAmount: number;
  expectedTransferAmount: number;
  expectedTotal: number;
  countedCashAmount?: number;
  countedTransferAmount?: number;
  differenceAmount: number;
  notes?: string;
  movements: PosCashMovement[];
  cashRegister?: PosCashRegister;
}

export interface PosCashRegister {
  id: number;
  uuid: string;
  name: string;
  code: string;
  isDefault: boolean;
  status: 'active' | 'inactive';
  location?: string;
  currentSession: PosCashSession | null;
}

export interface PosCashMovement {
  id: number;
  uuid: string;
  type: 'opening' | 'sale' | 'expense' | 'withdrawal' | 'cash_in' | 'adjustment';
  method: 'cash' | 'transfer';
  amount: number;
  currency: string;
  description?: string;
  reference?: string;
  occurredAt: string;
}

export interface PosOpenCashSessionPayload {
  openingCashAmount: number;
  notes?: string;
}

export interface PosCashMovementPayload {
  type: 'expense' | 'withdrawal' | 'cash_in' | 'adjustment';
  method: 'cash' | 'transfer';
  amount: number;
  description: string;
  reference?: string;
}

export interface PosCloseCashSessionPayload {
  countedCashAmount: number;
  countedTransferAmount: number;
  notes?: string;
}

export type CashScope = 'pos' | 'general';

export interface PosApiLoginResponse {
  token: string;
  user: PosApiUser;
  workspace: WorkspaceSummary;
  channel: PosApiChannel | null;
  cashRegister: PosCashRegister | null;
  cashRegisterSelectionRequired: boolean;
}

export interface PosApiUser {
  id: number;
  name: string;
  email: string;
  role: string;
  isSuperAdmin: boolean;
}

export interface PosApiChannel {
  id: number;
  uuid: string;
  name: string;
  type: string;
  settings: {
    posOrderMode: string;
    paymentMethods: string[];
  };
}

interface ApiEnvelope<T> {
  data: T;
  meta?: {
    current_page?: number;
    per_page?: number;
    total?: number;
    last_page?: number;
    selection_required?: boolean;
    selected_cash_register_id?: number | null;
  };
}

type ApiRecord = Record<string, unknown>;

interface PosRuntimeConfig {
  apiBaseUrl: string;
}

@Injectable({ providedIn: 'root' })
export class PosAuthService {
  readonly token = signal<string | null>(this.loadToken());

  setToken(token: string): void {
    this.token.set(token);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(tokenStorageKey, token);
    }
  }

  clearToken(): void {
    this.token.set(null);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(tokenStorageKey);
    }
  }

  setWorkspaceId(workspaceId: number): void {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(workspaceStorageKey, String(workspaceId));
    }
  }

  loadWorkspaceId(): number | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const value = Number(window.localStorage.getItem(workspaceStorageKey));

    return Number.isFinite(value) && value > 0 ? value : null;
  }

  setCashRegisterId(workspaceId: number, cashRegisterId: number): void {
    if (typeof window === 'undefined') {
      return;
    }

    const selections = this.loadCashRegisterSelections();
    selections[String(workspaceId)] = cashRegisterId;
    window.localStorage.setItem(cashRegisterStorageKey, JSON.stringify(selections));
  }

  clearCashRegisterId(workspaceId: number): void {
    if (typeof window === 'undefined') {
      return;
    }

    const selections = this.loadCashRegisterSelections();
    delete selections[String(workspaceId)];
    window.localStorage.setItem(cashRegisterStorageKey, JSON.stringify(selections));
  }

  loadCashRegisterId(workspaceId: number): number | null {
    const value = Number(this.loadCashRegisterSelections()[String(workspaceId)]);

    return Number.isFinite(value) && value > 0 ? value : null;
  }

  headers(workspaceId?: number, options: { includeCashRegister?: boolean } = {}): HttpHeaders {
    let headers = new HttpHeaders({
      Accept: 'application/json',
    });

    const token = this.token();

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    if (workspaceId) {
      headers = headers.set('X-Workspace-Id', String(workspaceId));

      const cashRegisterId = options.includeCashRegister === false
        ? null
        : this.loadCashRegisterId(workspaceId);

      if (cashRegisterId) {
        headers = headers.set('X-Cash-Register-Id', String(cashRegisterId));
      }
    }

    return headers;
  }

  private loadToken(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    return window.localStorage.getItem(tokenStorageKey);
  }

  private loadCashRegisterSelections(): Record<string, number> {
    if (typeof window === 'undefined') {
      return {};
    }

    try {
      const value: unknown = JSON.parse(window.localStorage.getItem(cashRegisterStorageKey) ?? '{}');

      return value && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, number>
        : {};
    } catch {
      return {};
    }
  }
}

@Injectable({ providedIn: 'root' })
export class PosApiService {
  private readonly http = injectHttpClient();
  private readonly auth = injectPosAuthService();
  private runtimeConfig: PosRuntimeConfig | null = null;

  async loadRuntimeConfig(): Promise<void> {
    if (this.runtimeConfig) {
      return;
    }

    try {
      const config = await firstValueFrom(
        this.http.get<Partial<PosRuntimeConfig>>('/assets/pos-config.json'),
      );

      this.runtimeConfig = {
        apiBaseUrl: typeof config.apiBaseUrl === 'string' ? config.apiBaseUrl : '',
      };
    } catch {
      this.runtimeConfig = {
        apiBaseUrl: '',
      };
    }
  }

  async login(payload: PosLoginPayload): Promise<PosApiLoginResponse> {
    const response = await firstValueFrom(
      this.http.post<ApiEnvelope<ApiRecord>>(`${this.baseUrl()}/login`, {
        email: payload.email,
        password: payload.password,
        device_name: payload.deviceName,
        workspace_id: payload.workspaceId,
      }),
    );

    const data = response.data;
    const token = stringValue(data['token']);
    this.auth.setToken(token);

    const workspace = mapWorkspace(recordValue(data['workspace']));
    this.auth.setWorkspaceId(workspace.id);

    return {
      token,
      user: mapUser(recordValue(data['user'])),
      workspace,
      channel: nullableRecordValue(data['channel']) ? mapChannel(recordValue(data['channel'])) : null,
      cashRegister: nullableRecordValue(data['cash_register'])
        ? mapCashRegister(recordValue(data['cash_register']))
        : null,
      cashRegisterSelectionRequired: booleanValue(data['cash_register_selection_required']),
    };
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post<ApiEnvelope<{ logged_out: boolean }>>(
          `${this.baseUrl()}/logout`,
          {},
          { headers: this.auth.headers() },
        ),
      );
    } catch (error) {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        throw error;
      }
    }

    this.auth.clearToken();
  }

  async workspaces(): Promise<WorkspaceSummary[]> {
    const response = await firstValueFrom(
      this.http.get<ApiEnvelope<ApiRecord[]>>(`${this.baseUrl()}/workspaces`, {
        headers: this.auth.headers(),
      }),
    );

    return response.data.map(mapWorkspace);
  }

  async config(workspaceId: number): Promise<{
    user: PosApiUser;
    workspace: WorkspaceSummary;
    channel: PosApiChannel | null;
    cashRegister: PosCashRegister | null;
    categories: PosCategory[];
  }> {
    const response = await firstValueFrom(
      this.http.get<ApiEnvelope<ApiRecord>>(`${this.baseUrl()}/config`, {
        headers: this.auth.headers(workspaceId),
      }),
    );
    const data = response.data;

    return {
      user: mapUser(recordValue(data['user'])),
      workspace: mapWorkspace(recordValue(data['workspace'])),
      channel: nullableRecordValue(data['channel']) ? mapChannel(recordValue(data['channel'])) : null,
      cashRegister: nullableRecordValue(data['cash_register'])
        ? mapCashRegister(recordValue(data['cash_register']))
        : null,
      categories: arrayValue(data['categories']).map((category) => mapCategory(recordValue(category))),
    };
  }

  async cashRegisters(workspaceId: number): Promise<{
    cashRegisters: PosCashRegister[];
    selectionRequired: boolean;
  }> {
    const response = await firstValueFrom(
      this.http.get<ApiEnvelope<ApiRecord[]>>(`${this.baseUrl()}/cash-registers`, {
        headers: this.auth.headers(workspaceId, { includeCashRegister: false }),
      }),
    );

    return {
      cashRegisters: response.data.map((register) => mapCashRegister(register)),
      selectionRequired: Boolean(response.meta?.selection_required),
    };
  }

  async products(workspaceId: number, params: { q?: string; category?: string; perPage?: number } = {}): Promise<PosProduct[]> {
    let query = new HttpParams().set('per_page', String(params.perPage ?? 100));

    if (params.q) {
      query = query.set('q', params.q);
    }

    if (params.category && params.category !== 'todos') {
      query = query.set('category', params.category);
    }

    const response = await firstValueFrom(
      this.http.get<ApiEnvelope<ApiRecord[]>>(`${this.baseUrl()}/products`, {
        headers: this.auth.headers(workspaceId),
        params: query,
      }),
    );

    return response.data.map(mapProduct);
  }

  async clients(workspaceId: number, params: { q?: string; perPage?: number } = {}): Promise<PosClient[]> {
    let query = new HttpParams().set('per_page', String(params.perPage ?? 20));

    if (params.q) {
      query = query.set('q', params.q);
    }

    const response = await firstValueFrom(
      this.http.get<ApiEnvelope<ApiRecord[]>>(`${this.baseUrl()}/clients`, {
        headers: this.auth.headers(workspaceId),
        params: query,
      }),
    );

    return response.data.map(mapClient);
  }

  async createClient(workspaceId: number, payload: PosCreateClientPayload): Promise<PosClient> {
    const response = await firstValueFrom(
      this.http.post<ApiEnvelope<ApiRecord>>(`${this.baseUrl()}/clients`, {
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        notes: payload.notes,
      }, {
        headers: this.auth.headers(workspaceId),
      }),
    );

    return mapClient(response.data);
  }

  async product(workspaceId: number, identifier: string | number): Promise<PosProduct> {
    const response = await firstValueFrom(
      this.http.get<ApiEnvelope<ApiRecord>>(`${this.baseUrl()}/products/${encodeURIComponent(String(identifier))}`, {
        headers: this.auth.headers(workspaceId),
      }),
    );

    return mapProduct(response.data);
  }

  async createOrder(workspaceId: number, payload: PosCreateOrderPayload): Promise<RecentPosOrder> {
    const response = await firstValueFrom(
      this.http.post<ApiEnvelope<ApiRecord>>(`${this.baseUrl()}/orders`, payload, {
        headers: this.auth.headers(workspaceId),
      }),
    );

    return mapOrder(response.data);
  }

  async addOrderPayment(workspaceId: number, orderUuid: string, payload: PosAddOrderPaymentPayload): Promise<RecentPosOrder> {
    const response = await firstValueFrom(
      this.http.post<ApiEnvelope<ApiRecord>>(`${this.baseUrl()}/orders/${encodeURIComponent(orderUuid)}/payments`, {
        method: payload.method,
        amount: payload.amount,
        status: payload.status,
        reference: payload.reference,
        notes: payload.notes,
      }, {
        headers: this.auth.headers(workspaceId),
      }),
    );

    return mapOrder(response.data);
  }

  async cashSession(workspaceId: number, scope: CashScope = 'pos'): Promise<PosCashSession | null> {
    const response = await firstValueFrom(
      this.http.get<ApiEnvelope<ApiRecord | null>>(`${this.baseUrl()}/cash-session`, {
        headers: this.cashHeaders(workspaceId, scope),
      }),
    );

    return response.data ? mapCashSession(response.data) : null;
  }

  async cashSessions(workspaceId: number, params: { perPage?: number; status?: 'open' | 'closed'; scope?: CashScope } = {}): Promise<PosCashSession[]> {
    const response = await firstValueFrom(
      this.http.get<ApiEnvelope<ApiRecord[]>>(`${this.baseUrl()}/cash-sessions`, {
        headers: this.cashHeaders(workspaceId, params.scope ?? 'pos'),
        params: new HttpParams()
          .set('per_page', String(params.perPage ?? 20))
          .set('status', params.status ?? 'closed'),
      }),
    );

    return response.data.map(mapCashSession);
  }

  async openCashSession(workspaceId: number, payload: PosOpenCashSessionPayload, scope: CashScope = 'pos'): Promise<PosCashSession> {
    const response = await firstValueFrom(
      this.http.post<ApiEnvelope<ApiRecord>>(`${this.baseUrl()}/cash-sessions/open`, {
        opening_cash_amount: payload.openingCashAmount,
        notes: payload.notes,
      }, {
        headers: this.cashHeaders(workspaceId, scope),
      }),
    );

    return mapCashSession(response.data);
  }

  async addCashMovement(workspaceId: number, sessionUuid: string, payload: PosCashMovementPayload, scope: CashScope = 'pos'): Promise<PosCashSession> {
    const response = await firstValueFrom(
      this.http.post<ApiEnvelope<ApiRecord>>(`${this.baseUrl()}/cash-sessions/${encodeURIComponent(sessionUuid)}/movements`, {
        type: payload.type,
        method: payload.method,
        amount: payload.amount,
        description: payload.description,
        reference: payload.reference,
      }, {
        headers: this.cashHeaders(workspaceId, scope),
      }),
    );

    return mapCashSession(response.data);
  }

  async closeCashSession(workspaceId: number, sessionUuid: string, payload: PosCloseCashSessionPayload, scope: CashScope = 'pos'): Promise<PosCashSession> {
    const response = await firstValueFrom(
      this.http.post<ApiEnvelope<ApiRecord>>(`${this.baseUrl()}/cash-sessions/${encodeURIComponent(sessionUuid)}/close`, {
        counted_cash_amount: payload.countedCashAmount,
        counted_transfer_amount: payload.countedTransferAmount,
        notes: payload.notes,
      }, {
        headers: this.cashHeaders(workspaceId, scope),
      }),
    );

    return mapCashSession(response.data);
  }

  async orders(workspaceId: number, params: { perPage?: number; sources?: CommercialOrderSource[] } = {}): Promise<RecentPosOrder[]> {
    const query = new HttpParams().set('per_page', String(params.perPage ?? 20));
    const sources = params.sources?.length ? params.sources : ['pos'];
    const requestQuery = query.set('sources', sources.join(','));
    const response = await firstValueFrom(
      this.http.get<ApiEnvelope<ApiRecord[]>>(`${this.baseUrl()}/orders`, {
        headers: this.auth.headers(workspaceId),
        params: requestQuery,
      }),
    );

    return response.data.map(mapOrder);
  }

  hasToken(): boolean {
    return Boolean(this.auth.token());
  }

  apiBaseUrl(): string {
    return this.baseUrl();
  }

  private cashHeaders(workspaceId: number, scope: CashScope): HttpHeaders {
    return this.auth.headers(workspaceId).set('X-Cash-Scope', scope);
  }

  private baseUrl(): string {
    if (typeof window === 'undefined') {
      return '/api/v1/pos';
    }

    const stored = window.localStorage.getItem(apiBaseStorageKey);

    if (stored) {
      return stored.replace(/\/$/, '');
    }

    if (this.runtimeConfig?.apiBaseUrl) {
      return this.runtimeConfig.apiBaseUrl.replace(/\/$/, '');
    }

    const hostname = window.location.hostname;

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://127.0.0.1:8001/api/v1/pos';
    }

    return `${window.location.origin}/api/v1/pos`;
  }
}

function mapUser(data: ApiRecord): PosApiUser {
  return {
    id: numberValue(data['id']),
    name: stringValue(data['name']),
    email: stringValue(data['email']),
    role: stringValue(data['role']),
    isSuperAdmin: booleanValue(data['is_super_admin']),
  };
}

function mapChannel(data: ApiRecord): PosApiChannel {
  const settings = recordValue(data['settings']);

  return {
    id: numberValue(data['id']),
    uuid: stringValue(data['uuid']),
    name: stringValue(data['name']),
    type: stringValue(data['type']),
    settings: {
      posOrderMode: stringValue(settings['pos_order_mode'], 'confirmed_when_paid'),
      paymentMethods: arrayValue(settings['payment_methods']).map((method) => String(method)),
    },
  };
}

export function mapWorkspace(data: ApiRecord): WorkspaceSummary {
  const slug = stringValue(data['slug']);

  return {
    id: numberValue(data['id']),
    uuid: optionalString(data['uuid']),
    name: stringValue(data['name']),
    slug,
    businessProfile: optionalString(data['business_profile']),
    currency: stringValue(data['currency'], 'MXN'),
    timezone: optionalString(data['timezone']),
    themeKey: themeKeyFromSlug(slug),
    capabilities: mapCapabilities(recordValue(data['capabilities'])),
    homeRoute: homeRouteValue(data['home_route']),
    posSettings: mapPosSettings(recordValue(data['pos_settings'])),
  };
}

function mapCapabilities(data: ApiRecord): WorkspaceSummary['capabilities'] {
  return {
    pos: booleanValue(data['pos']),
    repairs: booleanValue(data['repairs']),
    cash: booleanValue(data['cash']),
  };
}

function homeRouteValue(value: unknown): WorkspaceSummary['homeRoute'] {
  return value === '/repairs' || value === '/cash-session' || value === '/pos' ? value : '/pos';
}

export function mapCategory(data: ApiRecord): PosCategory {
  return {
    id: numberValue(data['id']),
    uuid: optionalString(data['uuid']),
    name: stringValue(data['name']),
    slug: stringValue(data['slug']),
    parentUuid: optionalString(data['parent_uuid']),
    sortOrder: numberValue(data['sort_order'], 0),
  };
}

export function mapProduct(data: ApiRecord): PosProduct {
  const category = nullableRecordValue(data['category']);
  const primaryImage = nullableRecordValue(data['primary_image']);

  return {
    id: numberValue(data['id']),
    uuid: optionalString(data['uuid']),
    sku: stringValue(data['sku']),
    barcode: optionalString(data['barcode']),
    slug: stringValue(data['slug']),
    name: stringValue(data['name']),
    type: productTypeValue(data['type']),
    categorySlug: category ? stringValue(category['slug']) : '',
    description: optionalString(data['description']),
    price: numberValue(data['price']),
    currency: stringValue(data['currency'], 'MXN'),
    unit: stringValue(data['unit'], 'pieza'),
    trackStock: booleanValue(data['track_stock']),
    stockQuantity: numberValue(data['stock_quantity']),
    available: booleanValue(data['available'], true),
    imageUrl: primaryImage ? optionalString(primaryImage['url']) : undefined,
    addons: arrayValue(data['addons']).map(mapAddon),
    bundleComponents: arrayValue(data['bundle_components']).map(mapBundleComponent),
  };
}

export function mapOrder(data: ApiRecord): RecentPosOrder {
  const customer = nullableRecordValue(data['customer']);
  const payments = arrayValue(data['payments']);
  const firstApprovedPayment = payments
    .map(recordValue)
    .find((payment) => stringValue(payment['status']) === 'approved');

  return {
    uuid: stringValue(data['uuid']),
    folio: stringValue(data['folio']),
    customerName: customer ? stringValue(customer['name'], 'Publico en general') : 'Publico en general',
    customerEmail: customer ? optionalString(customer['email']) : undefined,
    customerPhone: customer ? optionalString(customer['phone']) : undefined,
    source: orderSourceValue(data['source']),
    status: orderStatusValue(data['status']),
    paymentStatus: paymentStatusValue(data['payment_status']),
    total: numberValue(data['total']),
    paidTotal: numberValue(data['paid_total']),
    balanceDue: numberValue(data['balance_due']),
    method: firstApprovedPayment ? paymentMethodValue(firstApprovedPayment['method']) : undefined,
    createdAt: dateLabel(data['created_at']),
    items: arrayValue(data['items']).map((item) => mapOrderItem(recordValue(item))),
    payments: payments.map((payment) => mapOrderPayment(recordValue(payment))),
  };
}

export function mapClient(data: ApiRecord): PosClient {
  return {
    id: numberValue(data['id']),
    uuid: optionalString(data['uuid']),
    name: stringValue(data['name'], 'Cliente'),
    companyName: optionalString(data['company_name']),
    email: optionalString(data['email']),
    phone: optionalString(data['phone']),
    taxId: optionalString(data['tax_id']),
    notes: optionalString(data['notes']),
    status: stringValue(data['status'], 'active'),
  };
}

function mapOrderItem(item: ApiRecord): RecentPosOrder['items'][number] {
  return {
    id: numberValue(item['id']),
    name: stringValue(item['name']),
    quantity: numberValue(item['quantity']),
    unitPrice: numberValue(item['unit_price']),
    total: numberValue(item['total']),
    productId: optionalNumber(item['product_id']),
    productUuid: optionalString(item['product_uuid']),
    addons: arrayValue(item['addons']).map((addon) => recordValue(addon) as RecentPosOrder['items'][number]['addons'][number]),
  };
}

function mapOrderPayment(payment: ApiRecord): RecentPosOrder['payments'][number] {
  const method = stringValue(payment['method']);

  return {
    id: numberValue(payment['id']),
    type: stringValue(payment['type']),
    method: method === 'cash' || method === 'transfer' || method === 'card' ? method : 'other',
    amount: numberValue(payment['amount']),
    status: stringValue(payment['status']),
    reference: optionalString(payment['reference']),
  };
}

export function mapCashSession(data: ApiRecord): PosCashSession {
  const cashRegister = nullableRecordValue(data['cash_register']);

  return {
    id: numberValue(data['id']),
    uuid: stringValue(data['uuid']),
    folio: stringValue(data['folio']),
    status: stringValue(data['status']) === 'closed' ? 'closed' : 'open',
    openedByUser: optionalString(data['opened_by_user']),
    closedByUser: optionalString(data['closed_by_user']),
    openedAt: stringValue(data['opened_at']),
    closedAt: optionalString(data['closed_at']),
    openingCashAmount: numberValue(data['opening_cash_amount']),
    expectedCashAmount: numberValue(data['expected_cash_amount']),
    expectedTransferAmount: numberValue(data['expected_transfer_amount']),
    expectedTotal: numberValue(data['expected_total']),
    countedCashAmount: optionalNumber(data['counted_cash_amount']),
    countedTransferAmount: optionalNumber(data['counted_transfer_amount']),
    differenceAmount: numberValue(data['difference_amount']),
    notes: optionalString(data['notes']),
    movements: arrayValue(data['movements']).map(mapCashMovement),
    cashRegister: cashRegister ? mapCashRegister(cashRegister) : undefined,
  };
}

export function mapCashRegister(data: ApiRecord): PosCashRegister {
  const currentSession = nullableRecordValue(data['current_session']);

  return {
    id: numberValue(data['id']),
    uuid: stringValue(data['uuid']),
    name: stringValue(data['name'], 'Caja'),
    code: stringValue(data['code']),
    isDefault: booleanValue(data['is_default']),
    status: data['status'] === 'inactive' ? 'inactive' : 'active',
    location: optionalString(data['location']),
    currentSession: currentSession ? mapCashSession(currentSession) : null,
  };
}

function mapCashMovement(data: unknown): PosCashMovement {
  const movement = recordValue(data);
  const type = stringValue(movement['type']);
  const method = stringValue(movement['method']);

  return {
    id: numberValue(movement['id']),
    uuid: stringValue(movement['uuid']),
    type: type === 'opening' || type === 'sale' || type === 'expense' || type === 'withdrawal' || type === 'cash_in' || type === 'adjustment' ? type : 'adjustment',
    method: method === 'transfer' ? 'transfer' : 'cash',
    amount: numberValue(movement['amount']),
    currency: stringValue(movement['currency'], 'MXN'),
    description: optionalString(movement['description']),
    reference: optionalString(movement['reference']),
    occurredAt: stringValue(movement['occurred_at']),
  };
}

function mapPosSettings(data: ApiRecord): WorkspacePosSettings {
  const colors = recordValue(data['colors']);
  const darkColors = recordValue(data['dark_colors']);

  return {
    posName: stringValue(data['pos_name'], 'POS mostrador'),
    themeModeDefault: themeModeValue(data['theme_mode_default']),
    logoUrl: optionalString(data['logo_url']),
    colors: {
      primary: stringValue(colors['primary'], '#2563eb'),
      accent: stringValue(colors['accent'], '#f59e0b'),
      background: stringValue(colors['background'], '#f8fafc'),
      surface: stringValue(colors['surface'], '#ffffff'),
      text: stringValue(colors['text'], '#111827'),
      muted: optionalString(colors['muted']),
      border: optionalString(colors['border']),
      shadow: optionalString(colors['shadow']),
      input: optionalString(colors['input']),
    },
    darkColors: {
      background: stringValue(darkColors['background'], '#0f172a'),
      surface: stringValue(darkColors['surface'], '#182230'),
      text: stringValue(darkColors['text'], '#f8fafc'),
      muted: stringValue(darkColors['muted'], '#cbd5e1'),
      border: stringValue(darkColors['border'], 'rgba(255, 255, 255, 0.12)'),
      shadow: stringValue(darkColors['shadow'], 'rgba(0, 0, 0, 0.34)'),
      input: stringValue(darkColors['input'], '#111827'),
    },
  };
}

function mapAddon(data: unknown): PosProductAddon {
  const addon = recordValue(data);

  return {
    id: numberValue(addon['id']),
    name: stringValue(addon['name']),
    description: optionalString(addon['description']),
    priceDelta: numberValue(addon['price_delta']),
  };
}

function mapBundleComponent(data: unknown): PosBundleComponent {
  const component = recordValue(data);

  return {
    name: stringValue(component['name']),
    quantity: numberValue(component['quantity']),
    unit: stringValue(component['unit'], 'pieza'),
  };
}

function themeKeyFromSlug(slug: string): PosThemeKey {
  if (slug.includes('pollos') || slug.includes('taly')) {
    return 'taly';
  }

  if (slug.includes('universo')) {
    return 'universo';
  }

  return 'default';
}

function productTypeValue(value: unknown): PosProduct['type'] {
  return value === 'product' || value === 'service' || value === 'bundle' || value === 'supply' || value === 'custom'
    ? value
    : 'product';
}

function orderStatusValue(value: unknown): RecentPosOrder['status'] {
  return value === 'confirmed' || value === 'quoted' || value === 'cancelled' ? value : 'quoted';
}

function orderSourceValue(value: unknown): CommercialOrderSource {
  return value === 'ecommerce' || value === 'panel' || value === 'pos' ? value : 'pos';
}

function paymentStatusValue(value: unknown): RecentPosOrder['paymentStatus'] {
  return value === 'paid' || value === 'partial' || value === 'unpaid' ? value : 'unpaid';
}

function paymentMethodValue(value: unknown): RecentPosOrder['method'] {
  return value === 'cash' || value === 'transfer' ? value : undefined;
}

function themeModeValue(value: unknown): PosThemeMode {
  return value === 'dark' || value === 'system' || value === 'light' ? value : 'light';
}

function recordValue(value: unknown): ApiRecord {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as ApiRecord : {};
}

function nullableRecordValue(value: unknown): ApiRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as ApiRecord : null;
}

function arrayValue(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function stringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function numberValue(value: unknown, fallback = 0): number {
  const number = Number(value);

  return Number.isFinite(number) ? number : fallback;
}

function optionalNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const number = Number(value);

  return Number.isFinite(number) ? number : undefined;
}

function booleanValue(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function dateLabel(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0) {
    return 'Ahora';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

function injectHttpClient(): HttpClient {
  return inject(HttpClient);
}

function injectPosAuthService(): PosAuthService {
  return inject(PosAuthService);
}
